import { useCallback, useEffect, useRef, useState } from "react";
import { AlertOctagon, AlertTriangle, Bell, Info, X, type LucideIcon } from "lucide-react";
import { ApiError } from "../lib/api/client";
import { listNotifications, markNotificationsRead } from "../lib/api/endpoints";
import { formatDateTime } from "../lib/format";
import { isSafeActionUrl } from "../lib/safeUrl";
import { useSession } from "../lib/session/SessionContext";
import { C } from "../theme";
import type { NotificationDto, NotificationLevel, NotificationsPageDto } from "../types";

export { isSafeActionUrl };

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 60_000;

const LEVEL_META: Record<NotificationLevel, { icon: LucideIcon; label: string; color: string; background: string }> = {
  info: { icon: Info, label: "Informativo", color: C.gray, background: C.faint },
  warning: { icon: AlertTriangle, label: "Atenção", color: C.ink, background: C.limeSoft },
  critical: { icon: AlertOctagon, label: "Crítico", color: C.red, background: C.redSoft },
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

function NotificationItem({ notification, onAction }: { notification: NotificationDto; onAction: () => void }) {
  const meta = LEVEL_META[notification.level] ?? LEVEL_META.info;
  const Icon = meta.icon;
  const actionUrl = isSafeActionUrl(notification.actionUrl) ? notification.actionUrl : null;
  const external = actionUrl?.startsWith("http") ?? false;

  return (
    <li
      style={{
        display: "flex",
        gap: 10,
        padding: "11px 0",
        borderTop: `1px solid ${C.faint}`,
        opacity: notification.isRead ? 0.72 : 1,
      }}
    >
      <span
        aria-hidden="true"
        style={{ display: "inline-grid", placeItems: "center", flex: "0 0 auto", width: 26, height: 26, borderRadius: 8, color: meta.color, background: meta.background }}
      >
        <Icon size={14} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <strong style={{ minWidth: 0, color: C.ink, fontSize: 12.5, lineHeight: 1.4 }}>{notification.title}</strong>
          {!notification.isRead && (
            <span aria-label="Não lida" style={{ flex: "0 0 auto", width: 7, height: 7, borderRadius: "50%", background: C.lime }} />
          )}
        </div>
        <p style={{ margin: "3px 0 6px", color: C.gray, fontSize: 12, lineHeight: 1.5, overflowWrap: "anywhere" }}>{notification.message}</p>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, color: C.light, fontSize: 10.5 }}>
          <time>{formatDateTime(notification.publishedAtUtc)}</time>
          <span>{meta.label}</span>
          {actionUrl && (
            <a
              href={actionUrl}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              onClick={onAction}
              style={{ color: C.ink, fontWeight: 700, textDecoration: "underline", textDecorationColor: C.lime, textDecorationThickness: 2 }}
            >
              Abrir
            </a>
          )}
        </div>
      </div>
    </li>
  );
}

export function NotificationsBell() {
  const { accessToken, activeTenantId } = useSession();
  const token = accessToken ?? "";
  const [data, setData] = useState<NotificationsPageDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [readFailure, setReadFailure] = useState(false);
  const [marking, setMarking] = useState(false);
  const [open, setOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const markingRef = useRef(false);

  useEffect(() => {
    if (!token || !activeTenantId) return;
    let cancelled = false;
    listNotifications(token, { page: 1, pageSize: PAGE_SIZE })
      .then(result => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch(e => {
        if (!cancelled) setError(errorMessage(e, "Falha ao carregar notificações"));
      });
    return () => { cancelled = true; };
  }, [token, activeTenantId, reloadKey]);

  useEffect(() => {
    if (!token || !activeTenantId) return;

    const refresh = () => {
      listNotifications(token, { page: 1, pageSize: PAGE_SIZE })
        .then(result => {
          setData(result);
          setError(null);
        })
        .catch(() => {
          // Mantém a última lista conhecida; o erro de leitura é exposto ao abrir o painel.
        });
    };
    const onFocus = () => {
      if (document.visibilityState !== "hidden") refresh();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };

    const interval = window.setInterval(refresh, POLL_INTERVAL_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [token, activeTenantId]);

  const markRead = useCallback(async () => {
    if (!token || markingRef.current) return;
    markingRef.current = true;
    setMarking(true);
    setReadError(null);
    try {
      const result = await markNotificationsRead(token);
      setData(current =>
        current
          ? {
              ...current,
              unreadCount: result.unreadCount,
              lastReadAtUtc: result.lastReadAtUtc,
              items: current.items.map(item => ({ ...item, isRead: true })),
            }
          : current,
      );
      setReadFailure(false);
    } catch (e) {
      setReadError(errorMessage(e, "Não foi possível registrar a leitura."));
      setReadFailure(true);
    } finally {
      markingRef.current = false;
      setMarking(false);
    }
  }, [token]);

  useEffect(() => {
    if (!open || readFailure || !data) return;
    if (data.unreadCount > 0 || data.items.some(item => !item.isRead)) {
      void markRead();
    }
  }, [open, data, readFailure, markRead]);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && (panelRef.current?.contains(target) || buttonRef.current?.contains(target))) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setReadFailure(false);
      if ((data?.unreadCount ?? 0) > 0) void markRead();
    }
  };

  const closeAndFocusButton = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const unreadCount = data?.unreadCount ?? 0;
  const ariaLabel = unreadCount > 0
    ? `Notificações, ${unreadCount} não lida${unreadCount === 1 ? "" : "s"}`
    : "Notificações";

  return (
    <div className="notifications-bell">
      <button
        ref={buttonRef}
        type="button"
        className="notifications-bell__button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls="notifications-panel"
        title="Notificações"
        onClick={handleToggle}
      >
        <Bell aria-hidden="true" size={18} strokeWidth={1.8} />
        {unreadCount > 0 && (
          <span className="notifications-bell__badge" aria-hidden="true">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          id="notifications-panel"
          role="dialog"
          aria-label="Notificações"
          tabIndex={-1}
          className="notifications-bell__panel"
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, paddingBottom: 10 }}>
            <strong style={{ color: C.ink, fontSize: 13 }}>Notificações</strong>
            <button
              type="button"
              aria-label="Fechar notificações"
              onClick={closeAndFocusButton}
              style={{ display: "inline-grid", placeItems: "center", padding: 4, color: C.light, background: "transparent", border: 0, borderRadius: 6, cursor: "pointer" }}
            >
              <X aria-hidden="true" size={15} />
            </button>
          </div>

          {error && (
            <div role="alert" style={{ padding: "8px 0", color: C.red, fontSize: 11.5, lineHeight: 1.5 }}>
              <p style={{ margin: "0 0 6px" }}>{error}</p>
              <button type="button" onClick={() => setReloadKey(value => value + 1)} className="notifications-bell__retry">Tentar novamente</button>
            </div>
          )}

          {readError && (
            <div role="alert" style={{ padding: "8px 0", color: C.red, fontSize: 11.5, lineHeight: 1.5 }}>
              <p style={{ margin: "0 0 6px" }}>{readError}</p>
              <button
                type="button"
                onClick={() => { setReadFailure(false); void markRead(); }}
                disabled={marking}
                className="notifications-bell__retry"
              >
                {marking ? "Registrando..." : "Tentar novamente"}
              </button>
            </div>
          )}

          {data === null && !error ? (
            <div style={{ padding: "10px 0", color: C.light, fontSize: 12 }}>Carregando notificações...</div>
          ) : data && data.items.length === 0 ? (
            <div style={{ padding: "10px 0", color: C.light, fontSize: 12 }}>Nenhuma notificação no momento.</div>
          ) : data ? (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", maxHeight: 360, overflowY: "auto" }}>
              {data.items.map(notification => (
                <NotificationItem key={notification.id} notification={notification} onAction={() => setOpen(false)} />
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}
